import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Spinner } from '@chakra-ui/react';
import { FaYoutube } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import {
  EducationProfile,
  type ProfileSurface,
} from '../../../../app/_shared/educationProfile';
import { AvatarPhotoModal } from '../../../../app/components/AvatarPicker';
import { PublicProfileUrlField } from '../../../../app/components/PublicProfileUrlField';
import {
  FormControlCheckBoxGroup,
  FormControlInput,
  FormControlSelect,
  FormControlTextArea,
} from '../../../../app/components/FormControls';
import { TIMEZONE_OPTIONS } from '../../../../helpers/timezone.helper';
import { CategoryTreeField } from '../../../category';
import { ViewProfileButton } from '../../../public';
import { PictureByGender } from '../../../user/ui/components/PictureByGender';
import { NATIVE_LANGUAGE_CODES } from '../../application/types';
import { useLazyVerifyStudentSlugQuery } from '../../infrastructure/api/student-profile.api';
import { StudentProfileSummary } from '../components/StudentProfileSummary';
import {
  STUDENT_PROFILE_STEP_SECTION_IDS,
  studentAvatarList,
  useStudentProfileForm,
} from '../hooks/useStudentProfileForm';

const FIELD_WIDTH = '100%' as const;
const FIELD_HEIGHT = 40;

export const StudentProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const surface: Extract<ProfileSurface, 'edit' | 'pre-edit'> =
    pathname.includes('/pre-edit') ? 'pre-edit' : 'edit';
  const [verifySlug] = useLazyVerifyStudentSlugQuery();
  const {
    form,
    student,
    isLoading,
    isError,
    isSaving,
    canSubmit,
    canReset,
    stepsState,
    activeStepId,
    setActiveStepId,
    scrollToStep,
    onContinue,
    onCancel,
    displayName,
    timezoneLabel,
    avatar,
    isEditingPhoto,
    startPhotoEdit,
    confirmPhoto,
    onAvatarChange,
    pictureSrc,
    gender,
    categories,
    isCategoriesLoading,
  } = useStudentProfileForm();

  const picture = (
    <>
      <button
        type="button"
        className="profile-wrapper__picture-avatar profile-wrapper__picture-avatar--editable"
        onClick={startPhotoEdit}
        aria-label={t('student-profile.edit_photo')}
      >
        <PictureByGender picture={pictureSrc} gender={gender} />
      </button>
      <AvatarPhotoModal
        isOpen={isEditingPhoto}
        onClose={confirmPhoto}
        title="student-profile.edit_photo"
        value={avatar}
        avatarList={studentAvatarList}
        onApply={onAvatarChange}
      />
    </>
  );

  return (
    <EducationProfile
      isLoading={isLoading}
      isError={isError || !student}
      errorMessage={t('student-profile.load-error')}
      summary={
        <StudentProfileSummary
          surface={surface}
          displayName={displayName}
          timezoneLabel={timezoneLabel}
          percent={stepsState.percent}
          steps={stepsState.steps}
          activeStepId={activeStepId}
          onStepSelect={scrollToStep}
          onContinue={onContinue}
          picture={picture}
        />
      }
      title={t('student-profile.description')}
      skuLabel={t('student-profile.sku')}
      sku={student?.sku ?? ''}
      statusLabel={t('student-profile.status')}
      status={
        <span className="education-profile-page__status-badge">
          {student?.status}
        </span>
      }
      headerActions={
        <ViewProfileButton
          role="student"
          slug={student?.slug ?? ''}
          label={t('public-profile.view')}
          disabled={student?.status !== 'active' || !student}
        />
      }
      publicProfile={
        student ? (
          <PublicProfileUrlField
            publicPath={student.publicPath}
            slug={student.slug}
            canEdit={student.profile?.updatedAt == null}
            onVerify={async (slug) => verifySlug(slug).unwrap()}
          />
        ) : null
      }
      onSubmit={form.handleSubmit}
      onCancel={onCancel}
      cancelDisabled={isSaving || !canReset}
      isSaving={isSaving}
      submitDisabled={!canSubmit}
    >
      <section
        id={STUDENT_PROFILE_STEP_SECTION_IDS.basic}
        className="form-section form-section--basic"
      >
        <h2 className="form-section__title">
          {t('student-profile.steps.basic')}
        </h2>
        <FormControlInput
          name="displayName"
          form={form}
          label={t('student-profile.display_name')}
          isRequired
          isValidate
          width={FIELD_WIDTH}
          height={FIELD_HEIGHT}
          className="form-field"
          onFocus={() => setActiveStepId('basic')}
        />
        <FormControlTextArea
          name="bio"
          form={form}
          label={t('student-profile.bio')}
          isValidate
          rows={4}
          height={120}
          width={FIELD_WIDTH}
          className="form-field"
          onFocus={() => setActiveStepId('basic')}
        />
      </section>

      <section
        id={STUDENT_PROFILE_STEP_SECTION_IDS.goals}
        className="form-section form-section--experience"
      >
        <h2 className="form-section__title">
          {t('student-profile.steps.goals')}
        </h2>
        <FormControlTextArea
          name="goals"
          form={form}
          label={t('student-profile.goals')}
          isValidate
          rows={3}
          height={96}
          width={FIELD_WIDTH}
          className="form-field"
          onFocus={() => setActiveStepId('goals')}
        />
      </section>

      <section
        id={STUDENT_PROFILE_STEP_SECTION_IDS.categories}
        className="form-section form-section--skills"
      >
        <h2 className="form-section__title">
          {t('student-profile.steps.categories')}
        </h2>
        {isCategoriesLoading ? (
          <Spinner size="sm" />
        ) : categories.length === 0 ? (
          <p className="form-section__placeholder">
            {t('student-profile.categories_empty')}
          </p>
        ) : (
          <CategoryTreeField
            categories={categories}
            value={form.values.categoryIds}
            onChange={(next) => {
              setActiveStepId('categories');
              form.setFieldValue('categoryIds', next);
              form.setFieldTouched('categoryIds', true);
            }}
          />
        )}
      </section>

      <section
        id={STUDENT_PROFILE_STEP_SECTION_IDS.languages}
        className="form-section form-section--languages"
      >
        <h2 className="form-section__title">
          {t('student-profile.steps.languages')}
        </h2>
        <FormControlCheckBoxGroup
          name="nativeLanguages"
          form={form}
          label={t('student-profile.native_languages')}
          className="form-field"
          onFocus={() => setActiveStepId('languages')}
          options={NATIVE_LANGUAGE_CODES.map((code) => ({
            value: code,
            label: t(`student-profile.lang.${code}`),
          }))}
        />
      </section>

      <section className="form-section form-section--media">
        <h2 className="form-section__title">
          {t('student-profile.attachments')}
        </h2>
        <div className="form-section__grid form-section__grid--2">
          <FormControlInput
            name="introVideoUrl"
            form={form}
            label={t('student-profile.intro_video')}
            isValidate
            width={FIELD_WIDTH}
            height={FIELD_HEIGHT}
            className="form-field"
            leftIcon={
              <Box color="gray.500" fontSize="16px">
                <FaYoutube />
              </Box>
            }
          />
          <FormControlSelect
            name="timezone"
            form={form}
            label="student-profile.timezone"
            placeholder="student-profile.timezone_placeholder"
            options={TIMEZONE_OPTIONS}
            className="form-field"
            style={{ width: FIELD_WIDTH }}
            onChange={(value) => {
              form.setFieldValue('timezone', value == null ? '' : String(value));
            }}
          />
        </div>
      </section>
    </EducationProfile>
  );
};

export default StudentProfilePage;
